import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  withCredentials: true,
});

// Error message deduplication
const shownErrors = new Map<string, number>();
const ERROR_DEDUP_DURATION = 5000; // 5 seconds

const showErrorOnce = (message: string) => {
  const now = Date.now();
  const lastShown = shownErrors.get(message);
  
  if (!lastShown || now - lastShown > ERROR_DEDUP_DURATION) {
    shownErrors.set(message, now);
    toast.error(message);
  }
};

/** API `detail` may be a string, FastAPI validation array, or `{ message, code }` — never pass objects to toast. */
function formatApiErrorDetail(detail: unknown, fallback: string): string {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .filter((err: unknown) => err && typeof err === 'object' && typeof (err as { msg?: unknown }).msg === 'string')
      .map((err: unknown) => (err as { msg: string }).msg);
    return messages.length > 0 ? messages.join(', ') : fallback;
  }
  if (typeof detail === 'object') {
    const o = detail as Record<string, unknown>;
    if (typeof o.message === 'string') return o.message;
    if (typeof o.msg === 'string') return o.msg;
    if (typeof o.detail === 'string') return o.detail;
  }
  return fallback;
}

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;

      // Handle auth errors (401, 403)
      if (statusCode === 401 || statusCode === 403) {
        // Clear auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('email');
        localStorage.removeItem('premiumUntil');

        // Show error message (detail may be an object e.g. { message, code })
        showErrorOnce(
          formatApiErrorDetail(
            error.response?.data?.detail,
            'Authentication failed. Please login again.'
          )
        );

        // Redirect to login only if not a login request
        const isLoginRequest = error.config?.url?.includes('/dashboard-login');
        if (!isLoginRequest) {
          window.location.href = '/login';
        }
      }
      // Handle other 4xx errors
      else if (statusCode && statusCode >= 400 && statusCode < 500) {
        if (statusCode !== 404) {
          showErrorOnce(
            formatApiErrorDetail(error.response?.data?.detail, 'Request failed. Please try again.')
          );
        }
      }
      // Handle 5xx errors
      else if (statusCode && statusCode >= 500) {
        showErrorOnce('Something went wrong. Please try again later.');
      }
      // Handle network errors or other issues
      else {
        showErrorOnce('Network error. Please check your connection.');
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  premium_until: string | null;
  username: string | null;
  profile_picture: string | null;
  mobile: string | null;
  created_at: string;
}

export interface Post {
  id: number;
  user_id: number;
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT';
  platform_post_id: string;
  created_at: string;
  updated_at: string;
  category?: PostCategory | null;
  note?: string | null;
  archived?: boolean;
  is_favorite?: boolean;
  instagram_post: any | null;
  x_post: any | null;
  linkedin_post: any | null;
  tiktok_post: any | null;
  reddit_post: any | null;
  chrome_bookmark_post: any | null;
}

export interface PostCategory {
  id: string;
  category_enum:string;
  category_display_value: string;
  label:string;
}

export interface Category {
  id: string;
  category_enum:string;
  is_folder: boolean;
  category_display_value: string;
  post_count: number;
}

// Auth API endpoints
export const login = async (idToken: string): Promise<{
  name: string | undefined;
  access_token_expiry: number;
  country_code: string;
  mobile: string; access_token: string; user_id: string; email: string; premium_until: string; is_new_user: boolean;
  paddle_customer_id?: string;
}> => {
  const response = await api.post(`/dashboard-login?id_token_str=${idToken}`);
  return response.data;
};

// User API endpoints
export const fetchUserProfile = async (): Promise<UserProfile> => {
  const response = await api.get('/me');
  return response.data;
};

export const updateUserProfile = async (data: {
  mobile: string,
  country_code: string,
  country_name: string
}): Promise<UserProfile> => {
  const response = await api.put('/me', data);
  return response.data;
};

// Subscription / communication preferences
// GET returns array of { channel, type, is_active }; PUT body uses "types" (string) per item
export interface SubscriptionPreferenceItem {
  channel: string;
  type: string;
  is_active: boolean;
}

export interface SubscriptionPreferenceUpdateItem {
  channel: string;
  type: string;
  is_active: boolean;
}

export const fetchSubscriptionPreferences = async (): Promise<SubscriptionPreferenceItem[]> => {
  const response = await api.get('/subscription-preferences');
  return response.data;
};

export const updateSubscriptionPreferences = async (
  data: SubscriptionPreferenceUpdateItem[]
): Promise<void> => {
  await api.put('/subscription-preferences', data);
  // Response is only { message }; success = 200
};

// Posts API endpoints
export interface FetchPostsParams {
  search?: string;
  page?: number;
  sort_order?: 'asc' | 'desc';
  category?: string;
  label?: string;
  platform?: string | null;
  author_id?: string | null;
  date_from?: string;
  date_to?: string;
  archived?: boolean;
  favorites?: boolean;
}

// Helper function to transform API response to component-compatible format
const transformPostForComponents = (post: any) => {
  // Simply return the post as-is, preserving the original structure
  // Components will access data through instagram_post, x_post, linkedin_post, tiktok_post, and reddit_post objects
  return {
    id: post.id,
    user_id: post.user_id,
    platform: post.platform,
    platform_post_id: post.platform_post_id,
    created_at: post.created_at,
    updated_at: post.updated_at,
    category: post.category || null,
    note: post.note || null,
    is_archived: post.is_archived || false,
    is_favorite: post.is_favorite || false,
    instagram_post: post.instagram_post || null,
    x_post: post.x_post || null,
    linkedin_post: post.linkedin_post || null,
    tiktok_post: post.tiktok_post || null,
    reddit_post: post.reddit_post || null,
    chrome_bookmark_post: post.chrome_bookmark_post || null,
  };
};

export interface FetchPostsResponse {
  posts: Post[];
  count: number;
}

export const fetchPosts = async (params: FetchPostsParams): Promise<FetchPostsResponse> => {
  const response = await api.get('/posts', { params });
  // Transform the response to be compatible with existing components
  const transformedPosts = response.data.posts.map(transformPostForComponents);
  return {
    posts: transformedPosts,
    count: response.data.count
  };
};

export const updatePostNote = async (postId: number, note: string): Promise<{ success: boolean }> => {
  const response = await api.put(`/posts/${postId}/note`, { note });
  return response.data;
};

export const fetchPost = async (postId: number): Promise<Post> => {
  const response = await api.get(`/posts/${postId}`);
  return transformPostForComponents(response.data);
};

// Categories API endpoints
export const fetchCategories = async (): Promise<Category[]> => {
  const response = await api.get('/categories');
  return response.data;
};

export const fetchAllCategories = async (): Promise<Category[]> => {
  const response = await api.get('/categories/all');
  return response.data;
};

// Post counts API endpoint
export interface PostCounts {
  favorite_count: number;
  archived_count: number;
}

export const fetchPostCounts = async (): Promise<PostCounts> => {
  const response = await api.get('/posts/counts');
  return response.data;
};

export type PlatformCounts = Array<{ platform: string; count: number }>;

export const fetchPlatformCounts = async (): Promise<PlatformCounts> => {
  const response = await api.get('/posts/platform-counts');
  return response.data;
};

export const updatePostCategory = async (data: {
  post_id: number;
  category_id: number;
  label?: string;
}): Promise<{ success: boolean }> => {
  const response = await api.put(`/update-post-category`, data);
  return response.data;
};

// Post management API endpoints
export const deletePost = async (postId: number): Promise<{ success: boolean }> => {
  const response = await api.delete(`/posts/${postId}`);
  return response.data;
};

export const archivePost = async (postId: number): Promise<{ success: boolean }> => {
  const response = await api.put(`/posts/${postId}/archive`);
  return response.data;
};

export const unarchivePost = async (postId: number): Promise<{ success: boolean }> => {
  const response = await api.put(`/posts/${postId}/unarchive`);
  return response.data;
};

export const markPostfavorite = async (postId: number, isfavorite: boolean): Promise<{ message: string }> => {
  const response = await api.put(`/posts/${postId}/mark-favorite`, { is_favorite: isfavorite });
  return response.data;
};

// Bulk post management API endpoints
export interface BulkActionResponse {
  updated_count: number;
  failed_ids: number[];
}

export const bulkDeletePosts = async (postIds: number[]): Promise<BulkActionResponse> => {
  const response = await api.post(`/posts/bulk/delete`, { post_ids: postIds });
  return response.data;
};

export const bulkArchivePosts = async (postIds: number[], archive: boolean): Promise<BulkActionResponse> => {
  const response = await api.put(`/posts/bulk/archive`, { post_ids: postIds, archive });
  return response.data;
};

export const bulkMarkFavorite = async (postIds: number[], isFavorite: boolean): Promise<BulkActionResponse> => {
  const response = await api.put(`/posts/bulk/mark-favorite`, { post_ids: postIds, is_favorite: isFavorite });
  return response.data;
};

export const bulkUpdateCategory = async (
  postIds: number[],
  categoryId: number,
  label?: string
): Promise<BulkActionResponse> => {
  const payload: { post_ids: number[]; category_id: number; label?: string } = {
    post_ids: postIds,
    category_id: categoryId,
  };
  if (label) {
    payload.label = label;
  }
  const response = await api.put(`/posts/bulk/category`, payload);
  return response.data;
};

// Authors API endpoint
export interface Author {
  platform: string;
  name: string;
  username: string;
}

export const fetchAuthors = async (search?: string): Promise<Author[]> => {
  const params = search ? { search } : {};
  try {
    const response = await api.get('/posts/authors', { params });
    return response.data;
  } catch (error) {
    console.error('❌ fetchAuthors API error:', error);
    throw error;
  }
};

// Pricing types
export type PlanFrequency = 'MONTHLY' | 'YEARLY';

export interface PriceConfig {
  id: string;
  frequency: PlanFrequency;
  price: number;
  currency: string;
  paddle_price_id: string;
  paddle_product_id: string;
  created_at: string;
  updated_at: string;
}

// Enhanced pricing interface for display
export interface Feature {
  name: string;
  included: boolean;
}

export interface PricingPlan {
  plan_type: string;
  display_name: string;
  frequency: string;
  price: string;
  discounted_price?: string;
  currency: string;
  features: Feature[];
  trial_days: number;
  max_seats: number | null;
  special_text?: string;
  tag?: string;
  paddle_price_id?: string;
}

// Subscription types
export type SubscriptionStatus = 'active' | 'trialing' | 'paused' | 'past_due' | 'canceled' | 'expired';

export interface PaymentMethod {
  type: string;
  last_four_digits?: string;
  expiry_month?: number;
  expiry_year?: number;
  card_type?: string;
  cardholder_name?: string;
}

export interface SubscriptionPayment {
  id: number;
  paddle_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  payment_method_detail: PaymentMethod | null;
}

export interface Subscription {
  id: number;
  paddle_subscription_id: string;
  paddle_customer_id: string;
  amount: number;
  status: SubscriptionStatus;
  billing_period_start: string;
  billing_period_end: string;
  premium_until: string;
  valid_until?: string;
  created_at: string;
  updated_at: string;
  latest_payment: SubscriptionPayment;
  pricing_config: PricingPlan;
  cancel_subscription_link?: string;
  update_payment_method_link?: string;
  /** From plan-detail: plan limits and feature flags */
  posts_limit?: number;
  ai_tagging_enabled?: boolean;
  weekly_digests_enabled?: boolean;
  notion_export_enabled?: boolean;
  notion_export_sync_limit?: number;
  excel_export_enabled?: boolean;
  excel_export_limit?: number;
}

/** Raw response from GET /payments/plan-detail */
export interface PlanDetailResponse {
  id: number | null;
  plan_type: string;
  display_name: string;
  frequency: string;
  currency: string;
  paddle_price_id: string;
  paddle_product_id: string;
  paddle_subscription_id: string | null;
  paddle_customer_id: string | null;
  amount: number;
  status: string;
  billing_period_start: string | null;
  billing_period_end: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
  latest_payment: Partial<SubscriptionPayment> | Record<string, unknown>;
  posts_limit?: number;
  ai_tagging_enabled?: boolean;
  weekly_digests_enabled?: boolean;
  notion_export_enabled?: boolean;
  notion_export_sync_limit?: number;
  excel_export_enabled?: boolean;
  excel_export_limit?: number;
  cancel_subscription_link: string | null;
  update_payment_method_link: string | null;
}

// Pricing API endpoints
export const fetchPricingConfig = async (): Promise<PriceConfig[]> => {
  const response = await api.get('/payments/pricing-config');
  return response.data;
};

// Enhanced pricing data for display (transforms API data into UI-friendly format)
export const fetchPricingData = async (): Promise<PricingPlan[]> => {
  const response = await api.get('/payments/pricing-config');
  return response.data;
};

// Subscription / plan-detail API (used to determine active plan and subscription state)
// FREE plan is treated as no subscription so the app shows the same UI as when unsubscribed.
// Maps PlanDetailResponse to Subscription for backward compatibility with existing UI.
function mapPlanDetailToSubscription(res: PlanDetailResponse): Subscription {
  const latest = res.latest_payment && typeof res.latest_payment === 'object' && 'id' in res.latest_payment
    ? (res.latest_payment as SubscriptionPayment)
    : {
        id: 0,
        paddle_payment_id: '',
        amount: res.amount,
        currency: res.currency ?? 'USD',
        status: res.status,
        created_at: res.created_at,
        updated_at: res.updated_at,
        payment_method_detail: null,
      };
  const subscription: Subscription = {
    id: res.id ?? 0,
    paddle_subscription_id: res.paddle_subscription_id ?? '',
    paddle_customer_id: res.paddle_customer_id ?? '',
    amount: res.amount,
    status: res.status as SubscriptionStatus,
    billing_period_start: res.billing_period_start ?? '',
    billing_period_end: res.billing_period_end ?? '',
    premium_until: res.valid_until ?? '',
    valid_until: res.valid_until ?? undefined,
    created_at: res.created_at,
    updated_at: res.updated_at,
    latest_payment: latest,
    pricing_config: {
      plan_type: res.plan_type,
      display_name: res.display_name,
      frequency: res.frequency,
      price: String(res.amount),
      currency: res.currency,
      features: [],
      trial_days: 0,
      max_seats: null,
      paddle_price_id: res.paddle_price_id,
    },
    cancel_subscription_link: res.cancel_subscription_link ?? undefined,
    update_payment_method_link: res.update_payment_method_link ?? undefined,
    posts_limit: res.posts_limit,
    ai_tagging_enabled: res.ai_tagging_enabled,
    weekly_digests_enabled: res.weekly_digests_enabled,
    notion_export_enabled: res.notion_export_enabled,
    notion_export_sync_limit: res.notion_export_sync_limit,
    excel_export_enabled: res.excel_export_enabled,
    excel_export_limit: res.excel_export_limit,
  };
  return subscription;
}

export const fetchSubscription = async (): Promise<Subscription | null> => {
  try {
    const response = await api.get<PlanDetailResponse>('/payments/plan-detail');
    const data = response.data;
    if (data.plan_type?.toUpperCase() === 'FREE') {
      return null;
    }
    return mapPlanDetailToSubscription(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Sync status API endpoint
export type SyncStatus = 'PENDING' | 'PROCESSING' | 'PARTIALLY_PROCESSED' | 'SUCCESS';

export interface SyncStatusResponse {
  user_id: number;
  sync_status: SyncStatus;
  created_at: string;
  updated_at: string;
}

export const fetchSyncStatus = async (): Promise<SyncStatusResponse | null> => {
  try {
    const response = await api.get('/sync-status');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null; // Return null for 404 to indicate no sync status found
    }
    throw error;
  }
};

// Post Dumps types and API endpoints
export interface PostDumpsStatus {
  unprocessed_post_dumps_count: number;
  can_process: boolean;
  current_posts_count?: number;
  max_posts_limit: number;
}

export const fetchPostDumpsStatus = async (): Promise<PostDumpsStatus> => {
  const response = await api.get('/post-dumps-status');
  return response.data;
};

export const processPostDumps = async (): Promise<{ message: string }> => {
  const response = await api.post('/process-post-dumps');
  return response.data;
};

// AI categorisation API endpoints
export interface AiCategorisationEligibilityResponse {
  plan_eligible: boolean;
  posts_available_to_categorise: number;
}

export interface AiCategorisationStatusResponse {
  status: string;
}

export const fetchAiCategorisationEligibility = async (): Promise<AiCategorisationEligibilityResponse> => {
  const response = await api.get('/posts/ai-categorisation/eligibility');
  return response.data;
};

export const startAiCategorisation = async (): Promise<{ message?: string }> => {
  const response = await api.post('/posts/ai-categorisation');
  return response.data;
};

export const fetchAiCategorisationStatus = async (): Promise<AiCategorisationStatusResponse> => {
  const response = await api.get('/posts/ai-categorisation/status');
  return response.data;
};

// Promotion API endpoints
export interface PromotionAvailability {
  is_offer_claimed: boolean;
  is_promotion_credited: boolean;
}

export const checkPromotionAvailability = async (): Promise<PromotionAvailability> => {
  try {
    const response = await api.get('/promo/get');
    // If API call succeeds, offer is claimed
    return {
      is_offer_claimed: true,
      is_promotion_credited: !!response.data?.is_promotion_credited
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // 404 means offer is not claimed, and no promotion credited
      return {
        is_offer_claimed: false,
        is_promotion_credited: false
      };
    }
    throw error; // Re-throw other errors
  }
};

export const submitPromotion = async (postUrls: string[]): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/promo/create', { post_urls: postUrls });
  return response.data;
};

// Export API endpoint
export const exportPosts = async (): Promise<{ status: string; message: string }> => {
  const response = await api.post('/export/posts');
  return response.data;
};

// Instagram reel media API endpoint
export interface InstagramReelResponse {
  author: {
    name: string;
    username: string;
    profile_image: string;
  };
  media: Array<{
    type: "video" | "image";
    extension: string;
    url: string;
    quality: string;
    width: string;
    height: string;
  }>;
  caption: string;
  video_duration: number;
}

export const fetchInstagramReelMedia = async (postCode: string): Promise<InstagramReelResponse> => {
  const response = await api.get(`/instagram/${postCode}/media`);
  return response.data;
};

// Notion Integration API endpoints
export interface NotionIntegrationInitiateResponse {
  message: string;
  authorization_url: string;
  state: string;
}

export interface NotionIntegrationCallbackResponse {
  message: string;
  integration_id: number;
  database_url: string | null;
}

export interface NotionIntegrationStatus {
  is_connected: boolean;
  auto_sync_enabled: boolean;
  last_sync_at: string | null;
  database_url: string | null;
  integration_id: number | null;
}

export interface NotionSyncToggleResponse {
  message: string;
  integration_id: number;
}

export interface NotionManualSyncResponse {
  message: string;
  status: string;
  export_id: number;
}

export interface NotionDisconnectResponse {
  message: string;
  integration_id: number;
}

export interface NotionExportHistoryItem {
  export_id: number;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  export_type: string;
  created_at: string;
  error_message: string | null;
}

export interface NotionExportHistoryResponse {
  exports: NotionExportHistoryItem[];
}

// Notion Integration API functions
export const initiateNotionIntegration = async (redirectUri: string): Promise<NotionIntegrationInitiateResponse> => {
  const response = await api.post('/export/notion/integrate', {
    redirect_uri: redirectUri
  });
  return response.data;
};

export const completeNotionIntegration = async (code: string, state: string, redirectUri: string): Promise<NotionIntegrationCallbackResponse> => {
  const response = await api.post(`/export/notion/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&redirect_uri=${encodeURIComponent(redirectUri)}`);
  return response.data;
};

export const getNotionIntegrationStatus = async (): Promise<NotionIntegrationStatus> => {
  const response = await api.get('/export/notion/integration/status');
  return response.data;
};

export const toggleNotionAutoSync = async (autoSyncEnabled: boolean): Promise<NotionSyncToggleResponse> => {
  const response = await api.put('/export/notion/sync/toggle', {
    auto_sync_enabled: autoSyncEnabled
  });
  return response.data;
};

export const triggerNotionManualSync = async (exportType: string = 'manual'): Promise<NotionManualSyncResponse> => {
  const response = await api.post('/export/notion/sync/manual', {
    export_type: exportType
  });
  return response.data;
};

export const disconnectNotionIntegration = async (): Promise<NotionDisconnectResponse> => {
  const response = await api.post('/export/notion/disconnect');
  return response.data;
};

export const getNotionExportHistory = async (page: number = 1, limit: number = 5): Promise<NotionExportHistoryResponse> => {
  const response = await api.get(`/export/notion/export/history?page=${page}&limit=${limit}`);
  return response.data;
};

// Force Graph API endpoints
export type ForceGraphTheme = 'interests_map' | 'platforms' | 'timeline';

export interface ForceGraphNode {
  id: string;
  node_name: string;
  thumbnail_url: string | null;
  content: string | null;
  author: {
    image: string | null;
    name: string;
    username: string;
  } | null;
  cluster?: string;
  clusterId?: string;
  platform?: string;
}

export type ForceGraphLinkKind = 'bridge' | 'cross_cluster' | 'timeline' | 'platform_bridge' | 'intra_cluster';

export interface ForceGraphLink {
  source: string;
  target: string;
  kind?: ForceGraphLinkKind;
}

export interface ForceGraphCluster {
  cluster_name: string;
  nodes: ForceGraphNode[];
  links: ForceGraphLink[];
}

export interface ForceGraphResponse {
  clusters: ForceGraphCluster[];
}

export const fetchForceGraph = async (theme: ForceGraphTheme): Promise<ForceGraphResponse> => {
  const response = await api.get('/posts/force-graph', {
    params: { theme }
  });
  return response.data;
};

// Timeline Graph API endpoint
export interface TimelinePost {
  id: number;
  thumbnail_url: string | null;
  content: string | null;
  author: {
    image: string | null;
    name: string;
    username: string;
  };
  platform: string;
  platform_post_id: string;
  note: string | null;
  is_archived: boolean;
  is_favorite: boolean;
  category: {
    id: number;
    category_enum: string;
    category_display_value: string;
    label: string | null;
  } | null;
}

export interface TimelineGroup {
  timestamp: string; // Format: "November 26" or similar
  posts: TimelinePost[];
}

export interface TimelineGraphResponse {
  groups: TimelineGroup[];
}

export const fetchTimelineGraph = async (): Promise<TimelineGraphResponse> => {
  const response = await api.get('/posts/timeline-graph');
  return response.data;
};

// Wrapped (Instavault Recap) API endpoint
export type WrappedTimeSavedMethod = 'saved_item_conservative' | 'revisited_item_conservative';

export interface WrappedCategorySplitItem {
  category_id: string | number;
  category_enum: string;
  category_display_value: string;
  count: number;
  pct: number; // 0..100
}

export interface WrappedPlatformSplitItem {
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | (string & {});
  count: number;
  pct: number; // 0..100
}

export interface WrappedTasteShift {
  first_half: {
    category_enum: string | null;
    category_display_value: string | null;
    count: number;
    pct: number; // 0..100
  };
  second_half: {
    category_enum: string | null;
    category_display_value: string | null;
    count: number;
    pct: number; // 0..100
  };
}

export interface WrappedWeeksActiveItem {
  week_start: string; // ISO date (Monday)
  count: number;
}

export interface WrappedPeakSaveTime {
  hour_0_23: number; // 0..23
  label: string; // "9 PM" etc
  pct: number; // share of saves during this hour (0..100)
}

export interface WrappedFavouriteCreator {
  platform: string;
  name: string;
  username: string | null;
  profile_image: string | null;
  post_count: number;
}

export interface WrappedWorthRevisitingItem {
  post_id: number;
  platform: string;
  thumbnail_url: string | null;
  title: string | null;
  author_name: string | null;
  author_username: string | null;
  reason: string; // "Shared by others" / "High-signal bookmark" etc
}

export interface WrappedRabbitHoleItem {
  period: string; // e.g. "2025-09"
  count: number;
}

export interface WrappedRabbitHole {
  category_enum: string | null;
  category_display_value: string | null;
  total_count: number;
  spike_period: string | null; // e.g. "Sep 2025"
  series: WrappedRabbitHoleItem[];
}

export interface WrappedTimeSavedEstimate {
  method: WrappedTimeSavedMethod;
  seconds_per_item_range: [number, number];
  revisited_items: number;
  total_saves: number;
  seconds_range: [number, number];
  minutes_range: [number, number];
  hours_range: [number, number];
}

export interface WrappedPersona {
  id: 'signal_hunter' | 'archivist' | 'rabbit_holer' | 'collector' | 'maker' | (string & {});
  title: string;
  tagline: string;
  share_text: string;
}

export interface WrappedResponse {
  year: number;
  timezone: string;
  has_wrapped: boolean;
  generated_at: string; // ISO datetime
  range: {
    from: string; // ISO date
    to: string;   // ISO date
  };
  totals: {
    total_saves: number;
  };
  category_dna_top5: WrappedCategorySplitItem[];
  platform_mix: WrappedPlatformSplitItem[];
  taste_shift: WrappedTasteShift;
  longest_streak_days: number;
  longest_streak: {
    from: string; // ISO date
    to: string;   // ISO date
    days: number;
  } | null;
  weeks_active: {
    max_week_count: number;
    top_weeks: WrappedWeeksActiveItem[]; // sorted desc, capped
    sparkline_weeks: WrappedWeeksActiveItem[]; // full year weekly series
  };
  peak_save_time: WrappedPeakSaveTime;
  favourite_creator: WrappedFavouriteCreator | null;
  time_saved_estimate: WrappedTimeSavedEstimate;
  worth_revisiting: {
    count: number;
    sample_items: WrappedWorthRevisitingItem[]; // small sample; full list in app
  };
  biggest_rabbit_hole: WrappedRabbitHole | null;
  persona: WrappedPersona;
}

export const fetchWrapped = async (year: number): Promise<WrappedResponse> => {
  const response = await api.get('/wrapped', { params: { year } });
  return response.data;
};

// MCP OAuth 2.0 endpoints
export interface McpApproveParams {
  client_id: string;
  redirect_uri: string;
  code_challenge?: string;
  code_challenge_method?: string;
  state?: string;
}

export interface McpApproveResponse {
  code: string;
  state: string;
  redirect_uri: string;
  client_name: string;
}

export const approveMcpOAuth = async (params: McpApproveParams): Promise<McpApproveResponse> => {
  const response = await api.post('/mcp/oauth/authorize/approve', params);
  return response.data;
};

// Image proxy upload API endpoint
export interface ImageProxyUploadResponse {
  url: string; // The blob URL created from the returned image content
}

export const imageProxyUpload = async (
  url: string,
  postId: number,
  mediaType: 'thumbnail' | 'profile_picture'
): Promise<ImageProxyUploadResponse> => {
  const response = await api.get('/image-proxy-upload', {
    params: {
      url,
      post_id: postId,
      media_type: mediaType
    },
    responseType: 'blob' // API returns image content as blob
  });

  // Convert blob to URL that can be used in <img> tags
  const blobUrl = URL.createObjectURL(response.data);

  return { url: blobUrl };
};

export default api;
